import Vec2 from 'vec2';

import { CANVAS_OBJECT_TYPE_PREFIX, SIDE_ITEM_OPTIONS } from '~/core/constants';
import { ShapeType } from '~/core/enums';
import { getOutlinePts } from '~/core/helpers';
import {
  ArrowInfo,
  CornerInfo,
  DrawingAppliance,
  DrawingEdge,
  DrawingShape,
  ISerializableDrawingShape,
  RemovedEdgeAndCorners,
} from '~/core/models';
import {
  calculateEdgeConstraints,
  createDrawingShape,
  getArrowsFromOutlines,
  getInitialLabelIndexFromRemovedHistory,
  getLabelsForArrows,
  getLetterStartFromDrawingEdges,
} from '~/core/utils';

// TODO - remove after all old estimates with 'model' are expired
export const refactorModelToApplianceModel = (
  appliances: DrawingAppliance[]
): DrawingAppliance[] =>
  appliances.map((appliance) => {
    if ('model' in appliance && appliance.model) {
      const modifiedAppliance = {
        ...appliance,
        applianceModel: appliance.model,
      };

      delete modifiedAppliance.model;

      return modifiedAppliance;
    }

    return appliance;
  });

export const updateDShapeArrowsInfo = (
  dShape: DrawingShape,
  arrowInfo: ArrowInfo,
  index: number
) => {
  // drawingEdges got new id values on load, match them up
  const drawingEdgeId = dShape.drawingEdges[index]?.id;
  // check if the edge associated with this arrow has been "removed" because it's a wall/appliance/waterfall
  const removedEdge = dShape.removedElementsHistory?.edgesAndCorners?.find(
    (removedItem) => drawingEdgeId === removedItem.drawingEdge.id
  );
  const hasSideOption =
    removedEdge?.drawingEdge.sideItem.depth &&
    removedEdge?.drawingEdge.sideItem.type &&
    SIDE_ITEM_OPTIONS.includes(removedEdge.drawingEdge.sideItem.type);

  // hide the label if it's got a backsplash or waterfall
  const isVisibleInCanvas = !removedEdge || !hasSideOption;
  // and all of these are "non-finished" edges, so remove from calculations
  const isActiveForEdgeCalc = !removedEdge;

  const fixedArrow: ArrowInfo = {
    ...arrowInfo,
    drawingEdgeId,
    isVisibleInCanvas,
    isActiveForEdgeCalc,
  };

  return fixedArrow;
};

export const updateDShapeRemovedEdges = (
  dShape: DrawingShape,
  removedEdge: RemovedEdgeAndCorners
) => {
  const edge = dShape.drawingEdges.find(
    (edge) => edge.id === removedEdge.drawingEdge.id && edge.sideItem
  );

  if (edge) {
    removedEdge.drawingEdge = edge;
  } else {
    console.warn(
      'Removed edge not found for restore in useEstimate',
      removedEdge
    );
  }
};

export const updateDShapeDataWithCloneEstimateShape = (
  dShape: DrawingShape,
  initialArrows: ArrowInfo[],
  cloneEstimateShape: ISerializableDrawingShape
) => {
  dShape.arrowsInfo = initialArrows.map((arrowInfo, index) =>
    updateDShapeArrowsInfo(dShape, arrowInfo, index)
  );

  // if it was saved -> use the establishedLetterStartIndex
  // else -> figure out the best place for A (based on
  // the current position of the shape)
  dShape.establishedLetterStartIndex =
    cloneEstimateShape.establishedLetterStartIndex ??
    getInitialLabelIndexFromRemovedHistory(dShape) ??
    getLetterStartFromDrawingEdges(dShape);

  const labels = getLabelsForArrows(
    dShape.arrowsInfo,
    dShape.establishedLetterStartIndex
  );

  // map the new labels into the arrows
  dShape?.arrowsInfo?.forEach((arrowInfo: ArrowInfo, index) => {
    arrowInfo.label = labels[index];
  });

  // removed edges don't reference the actual drawingEdge objects, so we have to fix that
  dShape.removedElementsHistory?.edgesAndCorners?.forEach((removedEdge) =>
    updateDShapeRemovedEdges(dShape, removedEdge)
  );

  dShape.edge = cloneEstimateShape.edge;
  dShape.label = cloneEstimateShape.label;
  dShape.counterLabel = cloneEstimateShape.counterLabel;
};

const cloneCornerInfoForRestore = (source: CornerInfo): CornerInfo => ({
  ...source,
  coords: new Vec2(source.coords.x, source.coords.y),
  radiusCenterPoint: source.radiusCenterPoint
    ? new Vec2(source.radiusCenterPoint.x, source.radiusCenterPoint.y)
    : undefined,
  replacementStartCoords: source.replacementStartCoords
    ? new Vec2(source.replacementStartCoords.x, source.replacementStartCoords.y)
    : undefined,
  replacementEndCoords: source.replacementEndCoords
    ? new Vec2(source.replacementEndCoords.x, source.replacementEndCoords.y)
    : undefined,
});

const cloneDrawingEdgeForRestore = (source: DrawingEdge): DrawingEdge => {
  const clonedEdge = {
    ...source,
    startDisplayVec: new Vec2(
      source.startDisplayVec.x,
      source.startDisplayVec.y
    ),
    endDisplayVec: new Vec2(source.endDisplayVec.x, source.endDisplayVec.y),
    normalVec: new Vec2(source.normalVec.x, source.normalVec.y),
    dirVec: new Vec2(source.dirVec.x, source.dirVec.y),
    sideItem: {
      ...source.sideItem,
      arrowsInfo: source.sideItem.arrowsInfo.map((arrow) => ({
        ...arrow,
        path: arrow.path.map((vec) => new Vec2(vec.x, vec.y)),
      })),
    },
    bumpOut: source.bumpOut
      ? {
          ...source.bumpOut,
          arrowsInfo: source.bumpOut.arrowsInfo.map((arrow) => ({
            ...arrow,
            path: arrow.path.map((vec) => new Vec2(vec.x, vec.y)),
          })),
        }
      : undefined,
  };

  Object.defineProperty(clonedEdge, 'startDisplayVec', {
    value: clonedEdge.startDisplayVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'endDisplayVec', {
    value: clonedEdge.endDisplayVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'dirVec', {
    value: clonedEdge.dirVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'normalVec', {
    value: clonedEdge.normalVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  return clonedEdge;
};

const cloneApplianceForRestore = (
  source: DrawingAppliance
): DrawingAppliance => ({
  ...source,
  centerPoint: new Vec2(source.centerPoint.x, source.centerPoint.y),
});

export const createDShapeFromCloneEstimateShape = (
  cloneEstimateShape: ISerializableDrawingShape
) => {
  const dShape = createDrawingShape({
    skeleton: cloneEstimateShape.skeleton,
    type: ShapeType.PREDEFINED_SHAPE,
  });

  if (cloneEstimateShape.drawingEdges) {
    dShape.id = cloneEstimateShape.drawingEdges[0].drawingShapeId;
  }
  dShape.canvasObjectName =
    CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + dShape.id;

  const outlineData = getOutlinePts(dShape.skeleton);
  const outlinePoints = outlineData?.flatMap((item) =>
    item.coords ? [item.coords] : []
  );

  dShape.highlightPoints = outlinePoints;

  if (cloneEstimateShape.cornerMap) {
    Object.keys(cloneEstimateShape.cornerMap).forEach((key) => {
      dShape.cornerMap[key] = cloneCornerInfoForRestore(
        cloneEstimateShape.cornerMap[key]
      );
    });
  }

  if (cloneEstimateShape.drawingEdges) {
    dShape.drawingEdges = cloneEstimateShape.drawingEdges.map((edge) =>
      cloneDrawingEdgeForRestore(edge)
    );
  }

  dShape.removedElementsHistory = cloneEstimateShape.removedElementsHistory
    ? {
        edgesAndCorners:
          cloneEstimateShape.removedElementsHistory.edgesAndCorners?.map(
            (item) => ({
              ...item,
              drawingEdge: cloneDrawingEdgeForRestore(item.drawingEdge),
              arrowInfo: item.arrowInfo
                ? {
                    ...item.arrowInfo,
                    path: item.arrowInfo.path.map(
                      (vec) => new Vec2(vec.x, vec.y)
                    ),
                  }
                : undefined,
              appliedCorners: item.appliedCorners.map((corner) =>
                cloneCornerInfoForRestore(corner)
              ) as [CornerInfo, CornerInfo],
            })
          ) || [],
      }
    : undefined;

  // add initial arrows, correct edge ids
  const initialArrows = outlinePoints
    ? getArrowsFromOutlines(
        outlinePoints,
        outlinePoints,
        { diagonals: [], cornerIds: [] },
        []
      )
    : [];

  updateDShapeDataWithCloneEstimateShape(
    dShape,
    initialArrows,
    cloneEstimateShape
  );

  if (cloneEstimateShape.appliances?.length) {
    const refactoredAppliances = refactorModelToApplianceModel(
      cloneEstimateShape.appliances
    );

    dShape.appliances = refactoredAppliances.map((appliance) =>
      cloneApplianceForRestore(appliance)
    );
  }

  calculateEdgeConstraints(dShape);

  return dShape;
};
